import WhatsappIcon from '@mui/icons-material/WhatsApp'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import React from 'react'

import Select from '@/app/component/atom/form/select'
import IconText from '@/app/component/layout/icon-text'
import CustomDialog from '@/app/component/wrapper/custom-dialog'
import { cropText, iconMap, MESSAGE_TYPES } from '@/app/module/campaigns/definitions'
import { mapIndexed } from '@/app/module/campaigns/helpers'
import { LocalJumpAction, Part } from '@/app/module/campaigns/types'
import { getPartContentPreview } from '@/app/module/campaigns/utils/part-content'

type SavePayload = Pick<LocalJumpAction, 'jump'>

type Props = {
  index: number
  isNewAction: boolean
  parts: Part[]
  value: LocalJumpAction

  onClose: () => void
  onSave: (action: SavePayload) => void
}

type JumpQuestionMenuItemProps = {
  icon: string
  index: number
  label?: string
  message: string
  type: string
}

type SelectOption = {
  disabled: boolean
  label: React.ReactNode
  value: string
}

const unwrapValue = (value: LocalJumpAction): LocalJumpAction['jump'] => value.jump ?? { nextPart: '' }

const JumpQuestionMenuItem: React.FC<JumpQuestionMenuItemProps> = ({ icon, index, label, message, type }) => (
  <div
    style={{
      display: 'flex',
    }}
  >
    <span
      style={{
        marginRight: 5,
      }}
    >
      {icon === 'whatsapp' ? (
        <WhatsappIcon sx={{ width: '20px', height: '20px' }} />
      ) : (
        <Icon
          style={{
            color: 'rgba(0, 0, 0, 0.54)',
          }}
          fontSize="small"
        >
          {icon}
        </Icon>
      )}
    </span>
    {label ? (
      <Typography
        variant="subtitle2"
        color="textPrimary"
        style={{
          marginRight: 5,
        }}
      >
        {index}. {label}
      </Typography>
    ) : (
      <React.Fragment>
        <Typography
          variant="subtitle2"
          color="textPrimary"
          style={{
            marginRight: 5,
          }}
        >
          {index}. {type}
          {message && ':'}
        </Typography>
        <Typography variant="subtitle2" color="textSecondary">
          {cropText({
            text: message,
            limit: 60,
            firstCutOff: 30,
            secondCutOff: 25,
          })}
        </Typography>
      </React.Fragment>
    )}
  </div>
)

const selectContent = ({
  disabled,
  index,
  message,
}: {
  disabled: boolean
  index: number
  message: Part
}): SelectOption => {
  const messageType = MESSAGE_TYPES.find((type) => type.key in message)

  return {
    label: messageType ? (
      <JumpQuestionMenuItem
        icon={iconMap[messageType.key]}
        index={index + 1}
        label={message.label}
        message={getPartContentPreview(message)}
        type={messageType.label}
      />
    ) : null,
    value: message.id,
    disabled,
  }
}

const selectParts = (parts: Part[], currentIndex: number): SelectOption[] =>
  mapIndexed(
    (part, index) =>
      selectContent({
        message: part,
        index,
        disabled: index <= currentIndex,
      }),
    parts,
  )

const JumpToQuestion: React.FC<Props> = ({ index, onClose, onSave, parts: rawParts, value }) => {
  const { nextPart } = unwrapValue(value)
  const [partId, setPartId] = React.useState(nextPart)
  const parts = selectParts(rawParts, index)
  return (
    <CustomDialog open={true} onClose={onClose}>
      <DialogTitle style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <IconText>
            <Icon>account_tree</Icon>
            <Typography variant="h6">Jump to part</Typography>
          </IconText>
          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div
          style={{
            width: '80vw',
            minWidth: '300px',
            maxWidth: '100%',
          }}
        >
          <form
            id="send-message-form"
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <Typography variant="caption" color="textSecondary">
              We'll automatically ask the next question in line. But if you don't want that you can choose to jump to a
              later question in the campaign. The contact will be asked that question next, skipping any questions
              between this one and the one you select.
            </Typography>
            <Select
              className="select-jumpto-part"
              data-testid="part-select"
              label="Select Part"
              name="part-select"
              value={partId}
              values={parts}
              editable={true}
              onChange={({ value }) => {
                setPartId(value)
              }}
            />
          </form>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          id="jumpto-question-save-btn"
          disabled={!partId}
          onClick={() =>
            onSave({
              jump: {
                nextPart: partId,
              },
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </CustomDialog>
  )
}

export default JumpToQuestion
